/**
 * MarketplaceBrowsePage — /marketplace
 * Listings as plan-style cards on the ListPage grid variant. Demo pages: /pricing-tables (card: type, price, blurb,
 * one call to action, a highlights tick list) and /tables/data-tables (toolbar). Template example: NextAdmin
 * `(dashboard)/(home)/_component/last-transactions-table` header. Opening a card shows the App-Store-style detail
 * sheet (entity-management §5.2), where install and fork live behind `CanInstallMarketplace`.
 */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpCircle, Check, Globe, Lock, PlugZap, Store, Upload } from 'lucide-react';
import {
  listMarketplaceListingsOptions,
  listMarketplaceNeedsSetupOptions,
  listMarketplaceUpdatesOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { MarketplaceAcquisition, MarketplaceListing } from '../../generated/api/types.gen.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge, type BadgeVariant } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { Card } from '../../components/ui/Card.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { FilterPills } from '../../components/ui/FilterPills.js';
import { FilterSelect } from '../../components/ui/FilterSelect.js';
import { SearchBar } from '../../components/ui/SearchBar.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { Toolbar } from '../../components/ui/Toolbar.js';
import { MarketplaceDetailSheet } from '../../components/entity/MarketplaceDetailSheet.js';
import { marketplaceEntityLabel } from './marketplaceLabels.js';

const ENTITY_TYPES = [
  { id: 'all', label: 'All', type: undefined },
  { id: 'workflow', label: 'Workflows', type: 'workflow' },
  { id: 'agent_template', label: 'Templates', type: 'agent_template' },
  { id: 'tool', label: 'Tools', type: 'tool' },
  { id: 'workforce', label: 'Workforce', type: 'workforce' },
] as const;

const PRICING = [
  { id: 'all', label: 'Any price' },
  { id: 'free', label: 'Free' },
  { id: 'paid', label: 'Paid' },
] as const;

const ENTITY_BADGE_VARIANT: Record<string, BadgeVariant> = {
  workflow: 'info',
  agent_template: 'success',
  tool: 'warning',
  daemon: 'danger',
};

function CountPill({ count }: { count: number }) {
  return count > 0 ? <Badge variant="warning">{count}</Badge> : null;
}

export const MarketplaceBrowsePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requested = ENTITY_TYPES.find((t) => t.id === searchParams.get('type'))?.id ?? 'all';

  const [entityType, setEntityType] = useState<string>(requested);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [pricing, setPricing] = useState<string>('all');
  const [detail, setDetail] = useState<MarketplaceListing | null>(null);

  const type = ENTITY_TYPES.find((t) => t.id === entityType)?.type;
  const query = useQuery({
    ...listMarketplaceListingsOptions({
      query: {
        ...(type ? { type } : {}),
        ...(category ? { category } : {}),
        ...(search ? { search } : {}),
        ...(pricing === 'free' || pricing === 'paid' ? { pricing } : {}),
      },
    }),
    placeholderData: keepPreviousData,
  });
  const updatesQuery = useQuery(listMarketplaceUpdatesOptions());
  const needsSetupQuery = useQuery(listMarketplaceNeedsSetupOptions());

  const listings = query.data?.data ?? [];
  const updates = updatesQuery.data?.data ?? [];
  const needsSetup = needsSetupQuery.data?.data.length ?? 0;
  // What this organization acquired that its publisher changed since, by the entity each listing lists.
  const updateBySource = new Map(updates.map((update) => [update.sourceEntityId, update]));
  const categories = Array.from(new Set(listings.map((l) => l.category).filter((c): c is string => c !== null))).sort();

  const actions = (
    <>
      <Link
        to="/marketplace/updates"
        className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
      >
        <ArrowUpCircle size={14} aria-hidden="true" />
        Updates
        <CountPill count={updates.length} />
      </Link>
      <Link
        to="/marketplace/needs-setup"
        className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
      >
        <PlugZap size={14} aria-hidden="true" />
        Needs setup
        <CountPill count={needsSetup} />
      </Link>
      <Can policy="RequireSuperadmin">
        <Link
          to="/marketplace/publish"
          className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
        >
          <Upload size={14} aria-hidden="true" />
          Publish
        </Link>
      </Can>
    </>
  );

  return (
    <ListPage
      title="Marketplace"
      subtitle="Browse and install shared workflows, templates, tools and workforce configs."
      variant="grid"
      actions={actions}
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load marketplace listings"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            {query.error.message}
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search listings…" />}
          pills={
            <FilterPills
              aria-label="Listing type"
              options={ENTITY_TYPES.map(({ id, label }) => ({ id, label }))}
              value={entityType}
              onChange={setEntityType}
            />
          }
          right={
            <>
              <FilterSelect
                label="Category"
                anyLabel="All categories"
                value={category}
                options={categories.map((c) => ({ value: c, label: c }))}
                onChange={setCategory}
              />
              <FilterPills aria-label="Price" options={PRICING} value={pricing} onChange={setPricing} />
            </>
          }
        />
      }
    >
      {query.isPending && (
        <div
          className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
          role="status"
          aria-label="Loading listings"
        >
          {[0, 1, 2].map((i) => (
            <Card key={i} className="space-y-4">
              <Skeleton height={20} width={80} rounded />
              <Skeleton height={16} width="60%" />
              <Skeleton height={32} width="30%" />
              <Skeleton height={36} rounded="rounded-lg" />
            </Card>
          ))}
        </div>
      )}
      {!query.isPending && !query.isError && listings.length === 0 && (
        <Card>
          <EmptyState
            icon={Store}
            title="No listings match"
            description="Try a different search, or clear the type, category and price filters."
          />
        </Card>
      )}
      {listings.length > 0 && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {listings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              update={updateBySource.get(listing.entityId)}
              onOpen={() => setDetail(listing)}
            />
          ))}
        </div>
      )}
      <MarketplaceDetailSheet listing={detail} onClose={() => setDetail(null)} />
    </ListPage>
  );
};

function ListingCard({
  listing,
  update,
  onOpen,
}: {
  listing: MarketplaceListing;
  /** Set when this organization holds the listed entity at an older version than its publisher runs. */
  update: MarketplaceAcquisition | undefined;
  onOpen: () => void;
}) {
  const isFree = listing.pricingModel === 'free' || !listing.priceUsd;
  const isPublic = listing.contentAccess === 'public';
  const highlights = [listing.category, ...(listing.tags?.slice(0, 3) ?? [])].filter((h): h is string => !!h);

  return (
    <Card className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <Badge variant={ENTITY_BADGE_VARIANT[listing.entityType] ?? 'neutral'}>
          {marketplaceEntityLabel(listing.entityType)}
        </Badge>
        <span
          className="inline-flex items-center gap-1 text-xs text-muted"
          title={isPublic ? 'Readable and forkable' : 'Runnable only'}
        >
          {isPublic ? <Globe size={12} aria-hidden="true" /> : <Lock size={12} aria-hidden="true" />}
          {isPublic ? 'Public' : 'Private'}
        </span>
      </div>
      <div>
        <h3 className="text-base font-semibold text-heading">{listing.title}</h3>
        {listing.description && <p className="mt-1 line-clamp-2 text-sm text-muted">{listing.description}</p>}
      </div>
      <p className="text-3xl font-semibold text-heading">{isFree ? 'Free' : `$${listing.priceUsd}`}</p>
      <Button variant="secondary" onClick={onOpen} aria-label={`View ${listing.title}`}>
        View details
      </Button>
      {highlights.length > 0 && (
        <ul className="space-y-2 border-t border-card-border pt-4 text-sm text-body">
          {highlights.map((highlight) => (
            <li key={highlight} className="flex items-center gap-2">
              <Check size={14} className="shrink-0 text-success" aria-hidden="true" />
              {highlight}
            </li>
          ))}
        </ul>
      )}
      {update && (
        <Link
          to="/marketplace/updates"
          className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-warning hover:text-accent-text"
        >
          <ArrowUpCircle size={12} aria-hidden="true" />
          Update available · v{update.held.version} to v{update.latest.version}
        </Link>
      )}
    </Card>
  );
}

export default MarketplaceBrowsePage;
